/**
 * Resets the Design System with the provided object.
 * @param {object} obj - Object to reset the Design System with.
 * @param {object} [opts={}] - Options for resetting the Design System.
 */
export function setDesignSystem(obj, opts = {}) {
  const { version, ...value } = obj
  const designSystem = (this.getData().designSystem = value)

  this.sdk.updateData(['update', ['designSystem'], designSystem], {
    message: 'Reset Design System',
    ...opts
  })

  this.updateDesignSystemOnCanvas(this.getDesignSystem(), opts)
}

/**
 * Updates the Design System on the canvas.
 * @param {object} obj - Object containing the Design System data.
 */
export function updateDesignSystemOnCanvas(obj) {
  const Collection = this.getCanvasCollection()
  if (!Collection) {
    return
  }
  Collection.call('reinit', Collection.call('deepClone', obj), {
    preventInject: true
  })
  const vars = Collection.lookup('vars')
  if (vars?.update) {
    vars.update()
  }
}

/**
 * Retrieves the Design System data.
 * @param {string} [type] - Optional type to retrieve specific category data.
 * @returns {object} - The Design System data.
 */
export function getDesignSystem(type) {
  const designSystem = this.getData('designSystem')
  const defaultData = this.getBuiltins()
  if (type) {
    return {
      ...defaultData[type.toUpperCase()],
      ...designSystem[type.toUpperCase()]
    }
  }
  return designSystem
}

/**
 * Checks if a field is part of the built-in Design System.
 * @param {string} type - Category type (e.g., 'colors', 'typography').
 * @param {string} field - Field name to check.
 * @returns {boolean} - True if the field is part of the built-in Design System, false otherwise.
 */
export function isBuiltinDS(type, field) {
  if (!type || !field) {
    return false
  }

  const defaultData = this.getDefaults('DS_DEFAULTS') // await this.getDefaultDesignSystem()

  return Boolean(defaultData?.[type.toUpperCase()]?.[field])
}

/**
 * Updates a specific field in the Design System.
 * @param {string} key - Key to update (e.g., 'COLOR.primary', 'ICONS.secondary', 'THEME').
 * @param {*} val - Value to set for the key.
 * @param {object} [opts={}] - Options for updating the Design System.
 */
export async function updateDesignSystem(key, val, opts = {}) {
  const path = ['designSystem'].concat(
    this.call('isArray', key) ? key : key.split('.')
  )

  // capitalize keys
  if (/[a-z]/u.test(path[1])) {
    path[1] = path[1].toUpperCase()
  }

  this.sdk.updateData(val ? [['update', path, val]] : [['delete', path]], {
    message: val ? `Updated ${key}` : `Removed ${key}`,
    ...opts
  })

  const type = path[path.length - 2]
  // const key = path[path.length - 1]
  if (type === 'MEDIA') {
    this.getCanvas().update()
  } else {
    this.updateDesignSystemOnCanvas(this.getDesignSystem(), opts)
  }
  if (!opts.preventSidebarUpdate) {
    let sectionType = type.toLowerCase()
    if (opts.isSemanticColor) {
      sectionType = 'semantic_color'
    } else if (opts.isGlobalTheme) {
      sectionType = 'global_theme'
    } else if (opts.isTextStyles) {
      sectionType = 'templates'
    }

    const updatedSection = await this.call(
      'rerenderFileSidebarByType',
      sectionType
    )
    if (!updatedSection) {
      await this.getDynamicIsland()?.update()
    }
  }
}
/**
 * Updates a specific field in the Design System.
 * @param {*} changes Object Array { type: "update" | "delete", path: string[], value: any }
 */
export function bulkUpdateDesignSystem(changes, message, opts = {}) {
  if (!message || !changes) {
    return
  }

  const parsed = changes.map(({ type, path: p, value }) => {
    const path = p.includes('designSystem') ? p : ['designSystem', ...p]

    return value ? [type, path, value] : [type, path]
  })
  this.sdk.updateData(parsed, {
    message,
    ...opts
  })

  if (!opts.preventSidebarUpdate) {
    this.lookup('DynamicIsland')?.update()
  }
  this.updateDesignSystemOnCanvas(this.getDesignSystem(), opts)
}

export function activeAddingFloatingPanel(event, type) {
  const Section = this.call('findFileSidebarSection', type)

  if (!Section) {
    return
  }

  Section?.node?.scrollIntoView({
    behavior: 'smooth',
    inline: 'center',
    block: 'center'
  })

  const t = setTimeout(() => {
    const AddButton = Section.lookdown('IconButton.add')
    AddButton.onClick(event, AddButton, AddButton.state)
    clearTimeout(t)
  }, 350)
}
